import { useMemo } from 'react';

import {
  BlotterDensity,
  WLBuildCreditExposureInstancesWithSides,
  creditExposuresCompositeIDPipe,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useConstant,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type Column,
  type DateRangeFilter,
  type EnrichedCreditExposure,
  type WebsocketRequest,
} from '@talos/kyoko';
import { pick } from 'lodash-es';
import { pipe } from 'rxjs';
import { useCreditExposuresFilter } from '../../../../components/Filters';

const COLUMNS: Column[] = [
  { type: 'account', field: 'MarketAccount' },
  { type: 'currency', field: 'Currency' },
  {
    type: 'size',
    field: 'shortExposure',
    initialWidth: 200,
    params: { currencyField: 'ExposureCurrency' },
  },
  {
    type: 'size',
    field: 'shortExposureLimit',
    initialWidth: 200,
    params: { currencyField: 'ExposureCurrency' },
  },
  {
    type: 'size',
    field: 'longExposure',
    initialWidth: 200,
    params: { currencyField: 'ExposureCurrency' },
  },
  {
    type: 'size',
    field: 'longExposureLimit',
    initialWidth: 200,
    params: { currencyField: 'ExposureCurrency' },
  },
];

interface UseCreditExposuresBlotterProps {
  renderEmpty: () => JSX.Element;
}

const DEFAULT_SORT = '-Exposure';

export const useCreditExposuresBlotter = ({ renderEmpty }: UseCreditExposuresBlotterProps) => {
  const persistedBlotterTable = usePersistedBlotterTable('creditExposures');

  const filteredTrades = useCreditExposuresFilter({ persistedBlotterTable });
  const { blotterTableFilterProps, filterBuilderProps } = filteredTrades;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: false },
    filterBuilderProps,
  });

  const request = useMemo<WebsocketRequest>(
    () => ({
      name: 'Exposure',
      tag: 'BLOTTER_CREDIT_EXPOSURES',
      Throttle: '1s',
      sort_by: DEFAULT_SORT,
      ...removeEmptyFilters(onlyServerFilterKeys(filteredTrades.filter)),
    }),
    [filteredTrades.filter]
  );

  const compositePipe = useConstant(pipe(WLBuildCreditExposureInstancesWithSides, creditExposuresCompositeIDPipe));

  const blotterTable = useWsBlotterTable<WebsocketRequest, EnrichedCreditExposure>({
    request,
    columns: COLUMNS,
    persistence: persistedBlotterTable,
    rowID: 'rowID',
    flashRows: ['add'],
    density: BlotterDensity.Comfortable,
    pipe: compositePipe,
    gridOptions: {
      animateRows: true,
      rowHeight: 40,
      noRowsOverlayComponent: renderEmpty,
    },
  });

  return useMemo(
    () => ({ blotterTable, blotterTableFilterProps, filterBuilderAccordion }),
    [blotterTable, blotterTableFilterProps, filterBuilderAccordion]
  );
};

function onlyServerFilterKeys(filter: DateRangeFilter | undefined) {
  if (!filter) {
    return filter;
  }
  return pick(filter, ['EndDate', 'StartDate']);
}
